import * as React from "react"
import { type Flow, Interval, type ReportKind } from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import moneyFlowHelper from "@digits-shared/helpers/moneyFlowHelper"
import useConstant from "@digits-shared/hooks/useConstant"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import { v4 as generateUUID } from "uuid"
import { useHighlight } from "src/frontend/components/OS/Details/Shared/hooks/useHighlight"
import { BarChartComponent } from "src/frontend/components/Shared/Layout/Components/Charts/BarChartComponent"
import {
  type TimeseriesValue,
  toSortedTimeseriesValues,
  toTimeseriesValue,
} from "src/frontend/components/Shared/Layout/Components/Charts/toTimeseries"
import {
  additionalGradientStyles,
  useAgingChartAdditionalGradients,
} from "src/frontend/components/Shared/Layout/Components/Charts/useAgingChartAdditionalGradients"
import { ComponentExpandLink } from "src/frontend/components/Shared/Layout/Components/Headers/ComponentExpandIcon"
import {
  CONDENSED_TIME_COLUMNS,
  SECOND_LINE_COLUMNS,
  TIME_COLUMNS,
} from "src/frontend/components/Shared/Layout/Components/Statements/AgingReport/AgingColumns"
import { TileDelta } from "src/frontend/components/Shared/Layout/Components/Tiles/TileDelta"
import { ComponentSize } from "src/frontend/components/Shared/Layout/ComponentSize"
import { type SizingProps } from "src/frontend/components/Shared/Layout/types"
import { type useAgingSummaryChartLiveData } from "src/frontend/components/Shared/Portals/hooks/useAgingSummaryChartLiveData"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"

interface AgingChartComponentProps extends SizingProps {
  dimensionalSummary?: ReturnType<typeof useAgingSummaryChartLiveData>["dimensionalSummary"]
  componentSize: ComponentSize
  kind?: ReportKind
  includeTitleLink?: boolean
  title?: React.ReactNode | undefined
  skipAnimations: boolean
  loading: boolean
  flow: Flow
}

const SubheaderNode = styled.div`
  color: ${colors.secondary80};
`

const StyledBarChartComponent = styled(BarChartComponent)<{ chartId: string }>`
  ${additionalGradientStyles}
`

// Matches nothing in the real timeseries, keeping the chart from selecting the last bar
// by default.
function emptyTimeseriesValue(flow: Flow): TimeseriesValue {
  return {
    period: { endedAt: 0, interval: Interval.IntervalNone, name: "", startedAt: 0 },
    moneyFlow: moneyFlowHelper.buildZeroMoneyFlow(flow),
    deltaPrevious: undefined,
    deltaYearAgo: undefined,
  }
}

function indexToTitle(selectedIndex?: number, title?: React.ReactNode) {
  const text = selectedIndex === undefined ? title : TIME_COLUMNS[selectedIndex]
  if (typeof text === "string" && text?.endsWith("Days")) {
    return `${text} Overdue`
  }
  return text
}

export const AgingChartComponent: React.FC<AgingChartComponentProps> = ({
  dimensionalSummary,
  kind,
  includeTitleLink,
  componentSize,
  title = "Total Outstanding",
  height,
  skipAnimations,
  loading,
  flow,
}) => {
  const chartId = useConstant<string>(generateUUID)
  const pathGenerator = useFrontendPathGenerator()
  const highlight = useHighlight()
  const [selectedIndex, setSelectedIndex] = React.useState<number | undefined>(highlight)
  const onMouseOver = React.useCallback((value: TimeseriesValue, index: number) => {
    setSelectedIndex(index)
  }, [])

  const onMouseOut = React.useCallback(() => {
    setSelectedIndex(highlight ?? undefined)
  }, [highlight])

  const timeseries = React.useMemo(
    () => toSortedTimeseriesValues(dimensionalSummary?.aging),
    [dimensionalSummary?.aging]
  )

  // The aging chart uses gradients that are not used in other charts
  const additionalGradients = useAgingChartAdditionalGradients(chartId)

  const xAxisIntervalFormat = React.useCallback(
    (_value: number | Date, index: number) => {
      if (componentSize.isExtraLarge || componentSize.isLarge) {
        return TIME_COLUMNS[index] ?? ""
      }
      if (componentSize.isMedium || componentSize.isSmall) {
        return CONDENSED_TIME_COLUMNS[index] ?? ""
      }
      return ""
    },
    [componentSize]
  )

  const xAxisSecondLineFormat = React.useCallback(
    (_value: number | Date, index: number) => {
      switch (componentSize) {
        case ComponentSize.Medium:
        case ComponentSize.Small:
          return SECOND_LINE_COLUMNS[index] ?? ""
        default:
          return ""
      }
    },
    [componentSize]
  )

  const defaultValueNode = React.useMemo(() => {
    if (!dimensionalSummary) {
      return null
    }

    const amount =
      selectedIndex === undefined
        ? dimensionalSummary.summary.total.current?.total
        : dimensionalSummary.aging[selectedIndex]?.summary.total.current?.total ||
          moneyFlowHelper.buildZeroMoneyFlow(flow)

    const timeseriesToDisplay =
      selectedIndex === undefined
        ? toTimeseriesValue(dimensionalSummary.summary)
        : timeseries[selectedIndex]

    return (
      <>
        {moneyFlowHelper.currency(amount)}
        {componentSize === ComponentSize.Small && (
          <TileDelta timeseriesToDisplay={timeseriesToDisplay} />
        )}
      </>
    )
  }, [componentSize, dimensionalSummary, selectedIndex, timeseries, flow])

  const linkTitle = React.useMemo(
    () =>
      selectedIndex === undefined && includeTitleLink && kind ? (
        <ComponentExpandLink
          to={pathGenerator(routes.layoutComponentDetails, { configType: kind })}
          componentSize={componentSize}
        >
          {title}
        </ComponentExpandLink>
      ) : (
        indexToTitle(selectedIndex, title)
      ),
    [componentSize, pathGenerator, selectedIndex, title, kind, includeTitleLink]
  )

  if (!dimensionalSummary) {
    return null
  }

  const asOfTimestamp = dimensionalSummary.aging.toSorted(
    (a, b) => a.summary.period.startedAt - b.summary.period.startedAt
  )?.[0]?.summary.period.endedAt

  return (
    <StyledBarChartComponent
      loading={loading}
      chartId={chartId}
      defaultValueNode={defaultValueNode}
      dateCustomNode={
        asOfTimestamp ? (
          <SubheaderNode>
            as of {dateTimeHelper.displayNameFromUnixTimestamp(asOfTimestamp, Interval.Day)}
          </SubheaderNode>
        ) : null
      }
      componentSize={componentSize}
      title={linkTitle}
      icon={null}
      timeseries={timeseries}
      height={height}
      skipAnimations={skipAnimations}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      noDelta
      xAxisIntervalFormat={xAxisIntervalFormat}
      xAxisSecondLineFormat={xAxisSecondLineFormat}
      hideZeroXAxis={false}
      selectedIndex={selectedIndex ?? highlight}
      additionalGradients={additionalGradients}
      // Required to prevent the chart from highlighting the last bar by default
      preselectedValue={selectedIndex === undefined ? emptyTimeseriesValue(flow) : undefined}
    />
  )
}
